import React, { useMemo, useState } from 'react'

import { XIcon } from 'lucide-react'

import { Box, ButtonBase, Chip, Stack, Typography } from '@mui/material'

import { Card, Skeleton, ToggleButton } from '@repo/ui'

import { SelectField } from 'components/input/fields/SelectField'
import { Table } from 'components/display/Table/Table'
import { formatCount } from 'constants/referrals'
import {
  useInternalReferrals,
  useInternalReferralsGrouped,
  useInternalReferralSummary,
  type StaffReferralGroup,
  type StaffReferralStatus,
} from 'hooks/api/useInternalReferrals'

import { InviteRegionsCard } from './InviteRegionsCard'
import { StaffReferralsTable } from './StaffReferralsTable'
import { ReferralsSwitchedOffNotice } from 'components/feedback/ReferralsSwitchedOffNotice'

type View = 'all' | 'company' | 'user'

const VIEW_OPTIONS = [
  { label: 'All referrals', value: 'all' },
  { label: 'By company', value: 'company' },
  { label: 'By person', value: 'user' },
]

const RANGE_OPTIONS = [
  { label: 'All time', value: 'all' },
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 90 days', value: '90' },
  { label: 'Last 12 months', value: '365' },
]


/** A group's referrals bucketed the way staff ask about them. */
const bucket = (g: StaffReferralGroup) => {
  const n = (k: StaffReferralStatus) => g.by_status[k] ?? 0
  return {
    pending: n('sent') + n('signed_up') + n('granted'),
    joined: n('converted'),
    paying: n('paying') + n('capped'),
    ended: n('ended'),
    notCounted: n('ineligible') + n('superseded'),
  }
}

/**
 * VRG-658 — Signups → Referrals. A tab under Signups rather than its own sidebar row
 * (Spencer, VRG-658 open question 1: the internal rail already overflows).
 *
 * Three views of one set: every referral; grouped by referring company; grouped by the person
 * who sent it. A group's name drills into the full list filtered to that referrer. The summary
 * at the top is the GTM "Channels → Affiliates" feed (`GET /internal/referrals/summary`),
 * counted by referral creation date, so its figures reconcile with the list for the same range.
 */
export const ReferralsTab = () => {
  const [view, setView] = useState<View>('all')
  const [range, setRange] = useState('all')
  const [drill, setDrill] = useState<{ by: 'company' | 'user'; id: string; label: string } | null>(null)

  const from = useMemo(() => {
    if (range === 'all') return undefined
    const d = new Date()
    d.setDate(d.getDate() - Number(range))
    return d.toISOString().slice(0, 10)
  }, [range])

  const summary = useInternalReferralSummary(from ? { from } : {})
  const list = useInternalReferrals(
    {
      referrerCompanyId: drill?.by === 'company' ? drill.id : undefined,
      referrerUserId: drill?.by === 'user' ? drill.id : undefined,
    },
    { enabled: view === 'all' },
  )
  const grouped = useInternalReferralsGrouped(view === 'user' ? 'user' : 'company', { enabled: view !== 'all' })

  const s = summary.data
  const tiles = s
    ? [
        { label: 'Invitations sent', value: s.invites_sent },
        { label: 'Link signups', value: s.link_signups },
        { label: 'Became clients', value: s.became_client },
        { label: 'Paying', value: s.paying + s.capped },
        { label: 'Credits granted', value: s.credits_granted },
        { label: 'Credits reversed', value: s.credits_reversed },
        { label: 'Credits net', value: s.credits_net },
        { label: 'Insider leads', value: s.insider_attributed },
      ]
    : null

  const groupColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: view === 'user' ? 'Person' : 'Company',
        type: 'string',
        flex: 1,
        minWidth: 220,
        renderCell: ({ row }: { row: StaffReferralGroup }) => {
          const label = row.name ?? (view === 'user' ? row.email ?? 'Former member' : 'Former brand')
          return (
            <Stack sx={{ height: '100%', justifyContent: 'center', minWidth: 0 }}>
              <ButtonBase
                disabled={!row.key}
                onClick={() => {
                  if (!row.key) return
                  setDrill({ by: view === 'user' ? 'user' : 'company', id: row.key, label })
                  setView('all')
                }}
                sx={{ justifyContent: 'flex-start', '&:hover': { textDecoration: 'underline' } }}
              >
                <Typography variant="body2" noWrap>
                  {label}
                </Typography>
              </ButtonBase>
              {view === 'user' && (
                <Typography variant="body4" color="text.secondary" noWrap>
                  {[row.company_name, row.email].filter(Boolean).join(' · ')}
                </Typography>
              )}
            </Stack>
          )
        },
      },
      { field: 'total', headerName: 'Referrals', width: 120, type: 'number' },
      { field: 'pending', headerName: 'In progress', width: 150, type: 'number', valueGetter: (_: unknown, r: StaffReferralGroup) => bucket(r).pending },
      { field: 'joined', headerName: 'Joined', width: 110, type: 'number', valueGetter: (_: unknown, r: StaffReferralGroup) => bucket(r).joined },
      { field: 'paying', headerName: 'Paying', width: 110, type: 'number', valueGetter: (_: unknown, r: StaffReferralGroup) => bucket(r).paying },
      { field: 'not_counted', headerName: 'Not counted', width: 150, type: 'number', valueGetter: (_: unknown, r: StaffReferralGroup) => bucket(r).notCounted },
      { field: 'credits_net', headerName: 'Credits net', width: 140, type: 'number', valueFormatter: (v: number) => formatCount(v ?? 0) },
      { field: 'credits_reversed', headerName: 'Reversed', width: 120, type: 'number', valueFormatter: (v: number) => (v ? formatCount(v) : '—') },
    ],
    [view],
  )

  return (
    <Stack spacing={5}>
      {/* VRG-680 — staff still see the referrals; this says why nothing new arrives. */}
      <ReferralsSwitchedOffNotice mb={0} />
      <Stack direction="row" alignItems="center" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 3 }}>
        <ToggleButton
          tabs={VIEW_OPTIONS}
          tab={view}
          onChange={(v) => setView((v as View) ?? 'all')}
        />
        <Box sx={{ flex: 1 }} />
        <Box sx={{ width: 200 }}>
          <SelectField options={RANGE_OPTIONS} value={range} onChange={(v) => setRange(String(v))} />
        </Box>
      </Stack>

      {/* The Affiliates feed. Counted by referral creation date in the chosen range. */}
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
        }}
      >
        {(tiles ?? Array.from({ length: 8 }, () => null)).map((t, i) => (
          <Card key={t?.label ?? i} sx={{ p: 4, mb: 0 }}>
            {t ? (
              <>
                <Typography variant="body3" color="text.secondary">
                  {t.label}
                </Typography>
                <Typography sx={{ fontSize: 24, lineHeight: 1.2, mt: 1, fontVariantNumeric: 'tabular-nums' }}>
                  {formatCount(t.value)}
                </Typography>
              </>
            ) : (
              <>
                <Skeleton width={110} height={14} />
                <Skeleton width={50} height={26} sx={{ mt: 1 }} />
              </>
            )}
          </Card>
        ))}
      </Box>

      {view === 'all' ? (
        <Stack spacing={3}>
          {drill && (
            <Box>
              <Chip
                label={`Referred by ${drill.label}`}
                onDelete={() => setDrill(null)}
                deleteIcon={<XIcon size={14} />}
                variant="outlined"
              />
            </Box>
          )}
          <StaffReferralsTable rows={list.data?.data ?? []} loading={list.isLoading} height={560} />
        </Stack>
      ) : (
        <Table
          loading={grouped.isLoading}
          columns={groupColumns}
          rows={grouped.data?.data ?? []}
          getRowId={(row: StaffReferralGroup) => row.key ?? '__none__'}
          showSelection={false}
          alwaysShowRowSelection={false}
          height={560}
          paginationMode="client"
          initialPageSize={100}
        />
      )}

      <InviteRegionsCard />
    </Stack>
  )
}
